import { Container, Point } from 'pixi.js-legacy'
import { Shape } from "../../helpers/Shape";
import { graphic } from "../../Graphic";
import { containerEvents } from "../../ContainerEvents";
import { Entity } from "../../entity/Entity";
import {
    graphicEvents,
    REMOVE_ENTITIES,
    SELECT_ENTITIES,
    SET_SELECTION_PROPS,
    SET_SELECTION_PIVOT_LOCATION,
    UNSELECT_ENTITIES,
    UPDATE_SELECTION_PROPS,
    CREATE_ENTITIES,
    SET_LOADER,
    DUPLICATE_SELECTION,
    DELETE_SELECTION
} from "../../GraphicEvents";
import { MouseAreaSelection } from "./MouseAreaSelection";
import { TextService } from "../../entity/text/TextService";
import { Row } from "../../entity/row/Row";
import { Image as ImageEntity } from "../../entity/image/Image";
import { Button } from "../../helpers/Button";
import { stateManager } from "../../StateManager";

export class Selection {

    static PIVOT_LOCATION = {
        ENTITY: 'entity',
        SELECTION: 'selection',
    };

    constructor() {
        this.props = {};
        this.entities = [];
        this.translateArea = null;
        this.initialPosition = null;
        this.transformContainer = null;
        this.isTranslating = false;
        this.isRotating = false;
        this.globalPivot = null;
        this.mouseAreaSelection = new MouseAreaSelection();
        this.pivotLocation = Selection.PIVOT_LOCATION.SELECTION;

        graphicEvents.on(SET_SELECTION_PROPS, this.setSelectionProps.bind(this));
        graphicEvents.on(SET_SELECTION_PIVOT_LOCATION, this.setSelectionProps.bind(this));
        graphicEvents.on(DUPLICATE_SELECTION, () => {
            if (!this.isEmpty) this.duplicate();
        });
        graphicEvents.on(DELETE_SELECTION, () => this.onPressDelete());
    }

    get isEmpty() {
        return this.entities.length === 0;
    }

    get isInteracting() {
        return this.isTranslating || this.isRotating;
    }

    get hasOnlyImages() {
        return this.entities.every(entity => entity instanceof ImageEntity);
    }

    select(entities) {
        entities.forEach(entity => entity.onSelectEntity());
        this.entities.forEach(entity => entity.moveInWorldSpace());
        this.entities = [...entities, ...this.entities];
        this.update();
        graphicEvents.trigger(SELECT_ENTITIES, entities.map(entity => [{id: entity.id, type: entity.type}]))
    }

    unselect(entities, remove = false) {
        entities = TextService.removeEmpty(entities);

        entities.forEach(entity => entity.onUnselectEntity());
        graphicEvents.trigger(UNSELECT_ENTITIES, { entities, remove });

        if (remove) entities.forEach(entity => {
            graphic.entityManager.remove(entity);
            entity.destroy();
        });

        [...entities].forEach(entity => {
            const index = this.entities.indexOf(entity);
            if (index !== -1) {
                this.entities.splice(index, 1);
            }
        });

        if (!this.isEmpty) this.update();
    }

    update() {
        if (this.isEmpty) return;

        if (!this.container || !this.container.transform) this.container = new Container();
        if (!this.transformContainer || !this.transformContainer.transform) this.transformContainer = new Container();

        const { x, y, width, height } = Entity.getBoundsEntities(this.entities);
        this.globalPivot = new Point(
            Math.round(x.min + (width / 2)),
            Math.round(y.min + (height / 2))
        );

        this.container.x = x.min;
        this.container.y = y.min;

        this.transformContainer.pivot = this.globalPivot;
        this.transformContainer.position.x = this.globalPivot.x;
        this.transformContainer.position.y = this.globalPivot.y;

        this.entities.forEach(entity => {
            const { x, y, width, height } = Entity.getBoundsEntities([entity]);
            const globalPivot = new Point(x.min + (width / 2), y.min + (height / 2));

            entity.transformContainer = new Container();
            entity.transformContainer.pivot = globalPivot;
            entity.transformContainer.position.x = globalPivot.x;
            entity.transformContainer.position.y = globalPivot.y;
            entity.container.setParent(entity.transformContainer);
            entity.transformContainer.setParent(this.transformContainer);

            entity.drawSelectedArea();
        });

        this.container.setParent(this.transformContainer);
        graphic.container.addChildAt(this.transformContainer, this.hasOnlyImages ? 0 : (!!graphic.container.children.length ? 1 : 0));

        this.setupTranslate();
        this.setupRotate();

        this.updateProps();

        graphic.container.sortableChildren = true;
        this.transformContainer.zIndex = 50;

        this.rotateButton.update();
    }

    setupTranslate() {
        if (this.entities.length === 1) {
            const entity = this.entities[0];
            this.translateArea = entity.selectedArea;
        } else {
            if (this.translateArea) this.container.removeChild(this.translateArea);
            const { width, height } = Entity.getBoundsEntities(this.entities);
            this.translateArea = Shape.createRectangle(0, 0, width, height, Shape.selectionLineStyle, 0x4F84C4, 0.001);
            this.container.addChild(this.translateArea);
        }
        this.translateArea.interactive = true;
        this.translateArea.on('pointerdown', () => this.isTranslating = true);
    }

    setupRotate() {
        const { width } = Entity.getBoundsEntities(this.entities);

        if (this.rotateButton) this.rotateButton.destroy();

        this.rotateButton = new Button({
            resource: graphic.resources['rotate'],
            x: width / 2,
            y: -30,
            scale: 0.035,
            radius: 30,
            parent: this.container
        });
        this.rotateButton.draw();

        this.rotateButton.container.on('pointerdown', () => this.isRotating = true);
        this.container.addChild(this.rotateButton.container);
    }

    applyToTransformContainer(apply) {
        if (this.pivotLocation === Selection.PIVOT_LOCATION.ENTITY) {
            this.entities.forEach(entity => apply(entity.transformContainer));
            apply(this.transformContainer);
        } else if (this.pivotLocation === Selection.PIVOT_LOCATION.SELECTION) {
            apply(this.transformContainer);
        }
    }

    setSelectionProps({ x, y, angle }) {
        x = parseFloat(x);
        y = parseFloat(y);
        angle = parseFloat(angle);

        const sameRotation = this.entities.every(entity => entity.container.rotation === this.entities[0].container.rotation);
        let rotation = angle * Math.PI / 180;
        if (sameRotation) rotation -= this.entities[0].container.rotation;

        this.applyToTransformContainer(transformContainer => {
            if (typeof x !== 'undefined' && !isNaN(x)) transformContainer.x = x;
            if (typeof y !== 'undefined' && !isNaN(y)) transformContainer.y = y;
            if (typeof angle !== 'undefined' && !isNaN(angle)) transformContainer.rotation = rotation;
        });
    }

    setPivotLocation(pivotLocation) {
        this.pivotLocation = pivotLocation;
        this.updateProps();
        graphicEvents.trigger(UPDATE_SELECTION_PROPS, this.props);
    }

    updateProps() {
        let tc = null;
        this.applyToTransformContainer(transformContainer => {
            tc = transformContainer;
        });
        const toAngle = (rotation) => Math.floor(rotation * 180 / Math.PI);
        const sameRotation = this.entities.every(entity => toAngle(entity.container.rotation) === toAngle(this.entities[0].container.rotation));
        let angle = toAngle(tc.rotation);
        if (sameRotation) angle += toAngle(this.entities[0].container.rotation);

        this.props = {
            x: Math.floor(this.transformContainer.x * 10) / 10,
            y: Math.floor(this.transformContainer.y * 10) / 10,
            angle,
            scale: null
        };

        if (this.hasOnlyImages) {
            this.props.scale = this.entities[0].container.scale.x * 100;
        }
    }

    clear() {
        if (this.translateArea && this.translateArea.parent) {
            this.translateArea.parent.removeChild(this.translateArea);
        }

        this.applyToTransformContainer(transformContainer => {
            if (transformContainer) transformContainer.destroy();
        });

        if (this.transformContainer) this.transformContainer.destroy();
        if (this.container) this.container.destroy();

        this.entities.forEach(entity => entity.clearSelectedArea());
        graphic.container.removeChild(this.container);
    }

    destroy(remove = false) {
        if (this.isEmpty) return;

        this.saveEntities();
        this.clear();
        this.unselect(this.entities, remove);
    }

    saveEntities() {
        this.entities.forEach(entity => entity.moveInWorldSpace());
    }

    onStartClick() {
        this.initialPosition = containerEvents.mouseCoords;
        if (!this.isInteracting && !containerEvents.isPointerOverEntity) {
            this.destroy();
            this.mouseAreaSelection.isActive = true;
        }
        if (this.isInteracting) {
            stateManager.save();
        }
    }

    onStopClick() {
        this.initialPosition = null;
        this.isRotating = false;
        this.isTranslating = false;

        const { scale, ...props } = this.props;
        graphicEvents.trigger(UPDATE_SELECTION_PROPS, props);

        if (this.mouseAreaSelection.isActive) {
            const entities = this.mouseAreaSelection.getSelectedEntities();
//            console.log('entities', entities)
            this.select(entities);
            this.mouseAreaSelection.destroy();
        }
    }

    onMoveMouse() {
        if (this.isTranslating) this.translate();
        if (this.isRotating) this.rotate();

        if (this.mouseAreaSelection.isActive && this.initialPosition) {
            this.mouseAreaSelection.draw(this.initialPosition);
        }
    }

    onPressDelete() {
        if (this.isEmpty) return;

        this.entities.forEach(entity => {
            if (entity instanceof Row) {
                delete graphic.rowsSeats[entity.id];
                delete graphic.rowsLabel[entity.id];
            }
        });
        graphic.handleDuplicatedSeats();

        graphicEvents.trigger(REMOVE_ENTITIES, this.entities);
        this.destroy(true);
        this.entities.forEach(entity => entity.destroy());
    }

    onPressArrow({ detail: { originalEvent: e }}) {
        if (this.isEmpty || document.activeElement !== document.body) return;

        switch (e.keyCode) {
            case 37: {
                this.props.x -= 1;
                break;
            }
            case 38: {
                this.props.y -= 1;
                break;
            }
            case 39: {
                this.props.x += 1;
                break;
            }
            case 40: {
                this.props.y += 1;
                break;
            }
            default: { return; }
        }

        this.setSelectionProps(this.props);
        graphicEvents.trigger(UPDATE_SELECTION_PROPS, this.props);
    }

    translate() {
        const dist = {
            x: containerEvents.mouseCoords.x - this.initialPosition.x,
            y: containerEvents.mouseCoords.y - this.initialPosition.y
        };
        this.initialPosition = containerEvents.mouseCoords;

        this.transformContainer.x += dist.x;
        this.transformContainer.y += dist.y;

        this.updateProps();
    }

    rotate() {

        const getNorm = (obj = {x: 0, y: 0}) => Math.sqrt(obj.x * obj.x + obj.y * obj.y);

        const normalize = (u = {x: 0, y: 0}) => {
            let norm = getNorm(u);
            u.x = u.x / norm;
            u.y = u.y / norm;
            return u;
        };

        const convertDegToRad = (deg) => {
            return deg * Math.PI / 180.0;
        };

        let mouseWorld = new Point(containerEvents.mouseCoords.x, containerEvents.mouseCoords.y);
        let pivotWorld = new Point(this.transformContainer.x, this.transformContainer.y);

        let v1 = { x: 0, y: 1 };
        v1 = normalize(v1);

        let v2 = { x: mouseWorld.x - pivotWorld.x, y: mouseWorld.y - pivotWorld.y };
        v2 = normalize(v2);

        let alpha = Math.atan2(v2.y, v2.x) - Math.atan2(v1.y, v1.x);
        if (alpha < 0) { alpha += 2 * Math.PI; }
        alpha = alpha * (180 / Math.PI);
        alpha = convertDegToRad(alpha - 180);

        this.applyToTransformContainer(transformContainer => {
            transformContainer.rotation = alpha;
        });

        this.updateProps();
    }

    duplicate() {
        graphicEvents.trigger(SET_LOADER, true);
        const entities = this.entities.map(e => {
            const entity = e.clone();
            entity.moveInWorldSpace();
            const container = e.seatsContainer ? e.seatsContainer : e.container;
            entity.container.x = Math.round(entity.container.worldTransform.tx + container.width) + 50;
            entity.container.y = Math.round(entity.container.worldTransform.ty + container.height);
            console.log(entity.container.x)
            console.log(entity.container.y)
            delete entity.id;
            return entity;
        });
        graphicEvents.trigger(CREATE_ENTITIES, entities);
    }
}

export const selection = new Selection();